// pages/brand/EditBrand.js
import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import BrandForm from '../../components/brand/BrandForm';
import { ChevronLeft } from 'lucide-react';

function EditBrandPage() {
  const { id } = useParams();
  const { user, userRole } = useAuth();
  const navigate = useNavigate();
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    const fetchBrand = async () => {
      if (!id || !user) {
        setLoading(false);
        return;
      }

      try {
        const brandDocRef = doc(db, "brands", id);
        const brandSnapshot = await getDoc(brandDocRef);

        if (!brandSnapshot.exists()) {
          setError("Brand not found");
          setLoading(false);
          return;
        }

        const brandData = brandSnapshot.data();
        setBrand({ id, ...brandData });

        // Check if user is authorized to edit this brand
        // Admin can edit any brand, creator can edit their own brand
        if (userRole === 'admin' || brandData.createdBy === user.uid) {
          setAuthorized(true);
        } else {
          setError("You don't have permission to edit this brand");
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching brand:", err);
        setError("Failed to load brand data");
        setLoading(false);
      }
    };

    fetchBrand();
  }, [id, user, userRole]);

  if (loading) {
    return (
      <div className="p-4 max-w-6xl mx-auto flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <svg className="animate-spin h-10 w-10 text-blue-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600">Loading brand data...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-4 max-w-6xl mx-auto flex flex-col items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-blue-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <h2 className="text-2xl font-bold mb-2">Please Sign In</h2>
          <p className="text-gray-600 mb-6">You need to be logged in to edit brands</p>
          <button 
            onClick={() => navigate('/')} 
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 max-w-6xl mx-auto">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md mb-6">
          <p className="text-red-700">{error}</p>
        </div>
        <Link to="/mybrands" className="text-blue-600 hover:underline flex items-center">
          <ChevronLeft size={16} className="mr-1" /> Back to My Brands
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="px-4 max-w-4xl mx-auto mt-4 mb-6">
        <Link 
          to="/mybrands" 
          className="inline-flex items-center text-blue-600 hover:text-blue-800"
        >
          <ChevronLeft size={16} className="mr-1" />
          Back to My Brands
        </Link>
      </div>
      
      {authorized ? (
        <BrandForm brandId={id} />
      ) : (
        <div className="p-4 max-w-6xl mx-auto">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md mb-6">
            <p className="text-red-700">You don't have permission to edit this brand</p>
          </div>
          <Link to="/mybrands" className="text-blue-600 hover:underline flex items-center">
            <ChevronLeft size={16} className="mr-1" /> Back to My Brands
          </Link>
        </div>
      )}
    </div>
  );
}

export default EditBrandPage;